import { BasePaymentProvider } from './BasePaymentProvider.js';

function CloudPaymentsProvider(config) {
    BasePaymentProvider.call(this, config);
    this.name = 'cloud_payments';
    this.widgetLoaded = false;
    this.cryptogramLoaded = false;
    this.scriptLoadPromise = null;
}

CloudPaymentsProvider.prototype = Object.create(BasePaymentProvider.prototype);
CloudPaymentsProvider.prototype.constructor = CloudPaymentsProvider;

/**
 * Валидация конфигурации CloudPayments
 */
CloudPaymentsProvider.prototype.validateConfig = function() {
    var errors = [];

    if (!this.config.credentials || typeof this.config.credentials !== 'object') {
        errors.push('Cloud Payments credentials are required');
        return errors;
    }

    if (!this.config.credentials.publicKey) {
        errors.push('Cloud Payments public key is required');
    }

    if (!this.config.credentials.apiSecret) {
        errors.push('Cloud Payments API secret is required');
    }

    return errors;
};

/**
 * Инициализация провайдера - загрузка скриптов
 */
CloudPaymentsProvider.prototype.initialize = function() {
    var self = this;

    return new Promise(function(resolve, reject) {
        var configErrors = self.validateConfig();
        if (configErrors.length > 0) {
            reject(new Error('Cloud Payments configuration error: ' + configErrors.join(', ')));
            return;
        }

        Promise.all([
            self.loadWidget(),
            self.loadCryptogram()
        ]).then(function() {
            self.initialized = true;
            resolve();
        }).catch(function(error) {
            console.error('Failed to load Cloud Payments scripts:', error);
            reject(error);
        });
    });
};

/**
 * Загрузка виджета CloudPayments
 */
CloudPaymentsProvider.prototype.loadWidget = function() {
    var self = this;

    return new Promise(function(resolve, reject) {
        if (self.widgetLoaded || window.CloudPayments) {
            self.widgetLoaded = true;
            resolve();
            return;
        }

        var script = document.createElement('script');
        script.src = 'https://widget.cloudpayments.ru/bundles/cloudpayments';
        script.async = true;

        script.onload = function() {
            self.widgetLoaded = true;
            resolve();
        };

        script.onerror = function() {
            reject(new Error('Failed to load Cloud Payments widget'));
        };

        document.head.appendChild(script);
    });
};

/**
 * Загрузка криптограммы для токенизации карт
 */
CloudPaymentsProvider.prototype.loadCryptogram = function() {
    var self = this;

    return new Promise(function(resolve, reject) {
        if (self.cryptogramLoaded || window.cp) {
            self.cryptogramLoaded = true;
            resolve();
            return;
        }

        var script = document.createElement('script');
        script.src = 'https://widget.cloudpayments.ru/bundles/cryptogram';
        script.async = true;

        script.onload = function() {
            self.cryptogramLoaded = true;
            resolve();
        };

        script.onerror = function() {
            reject(new Error('Failed to load Cloud Payments cryptogram'));
        };

        document.head.appendChild(script);
    });
};

/**
 * Инициализация платежа (вызывается из PaymentContext)
 * Здесь мы возвращаем данные для бэкенда, а не выполняем реальный платеж
 */
CloudPaymentsProvider.prototype.initPayment = function(orderData, userData) {
    var self = this;

    return new Promise(function(resolve, reject) {
        // Проверяем, что провайдер инициализирован
        if (!self.isInitialized()) {
            self.initialize().then(function() {
                self._preparePaymentData(orderData, userData).then(resolve).catch(reject);
            }).catch(reject);
        } else {
            self._preparePaymentData(orderData, userData).then(resolve).catch(reject);
        }
    });
};

/**
 * Подготовка данных для платежа
 */
CloudPaymentsProvider.prototype._preparePaymentData = function(orderData, userData) {
    var self = this;

    return new Promise(function(resolve, reject) {
        var validationErrors = self.validatePaymentData(orderData, userData);
        if (validationErrors.length > 0) {
            reject(new Error('Payment data validation failed: ' + validationErrors.join(', ')));
            return;
        }

        // Возвращаем данные для бэкенда
        // Реальный платеж будет выполнен через виджет после того,
        // как бэкенд вернет checkoutUrl или другие данные
        resolve({
            success: true,
            orderId: orderData.id,
            amount: orderData.amount,
            provider: self.name,
            redirectRequired: false, // CloudPayments использует виджет, не редирект
            paymentData: {
                orderId: orderData.id,
                amount: orderData.amount,
                description: orderData.description,
                userId: userData.id,
                userEmail: userData.email,
                userPhone: userData.phone,
                items: orderData.items,
                restaurantName: orderData.restaurantName
            }
        });
    });
};

/**
 * Выполнение реального платежа через виджет CloudPayments
 * Этот метод вызывается из виджета после нажатия кнопки "Оплатить"
 */
CloudPaymentsProvider.prototype.executePayment = function(paymentData) {
    var self = this;

    return new Promise(function(resolve, reject) {
        if (!window.CloudPayments) {
            reject(new Error('Cloud Payments widget not loaded'));
            return;
        }

        var cp = new window.CloudPayments();

        var paymentConfig = {
            publicId: self.config.credentials.publicKey,
            description: paymentData.description || 'Заказ #' + paymentData.orderId,
            amount: paymentData.amount,
            currency: 'RUB',
            invoiceId: paymentData.orderId,
            accountId: String(paymentData.userId || ''),
            email: paymentData.userEmail || '',
            skin: (self.config.options && self.config.options.skin) || 'modern',
            data: {
                CloudPayments: {
                    customerReceipt: self.generateCustomerReceipt(paymentData)
                },
                orderId: paymentData.orderId,
                restaurantId: self.config.restaurantId,
                userId: paymentData.userId,
                testMode: self.config.testMode || false
            }
        };

        // В тестовом режиме используем специальные параметры
        if (self.config.testMode) {
            paymentConfig.data.CloudPayments.testMode = true;
        }

        cp.charge(paymentConfig,
            function(result) {
                resolve({
                    success: true,
                    transactionId: result.transactionId,
                    token: result.token,
                    cardHolderMessage: result.cardHolderMessage,
                    provider: self.name,
                    testMode: self.config.testMode,
                    paymentData: result,
                    amount: paymentData.amount,
                    orderId: paymentData.orderId
                });
            },
            function(reason, data) {
                reject(self.handleCloudPaymentsError(reason, data));
            }
        );
    });
};

/**
 * Подтверждение платежа (вызывается после успешного выполнения)
 */
CloudPaymentsProvider.prototype.confirmPayment = function(paymentData) {
    return Promise.resolve({
        success: true,
        paymentId: paymentData.transactionId,
        status: 'confirmed'
    });
};

/**
 * Отмена платежа
 */
CloudPaymentsProvider.prototype.cancelPayment = function(paymentId) {
    return Promise.resolve({
        success: true,
        paymentId: paymentId,
        status: 'cancelled'
    });
};

/**
 * Получение статуса платежа
 */
CloudPaymentsProvider.prototype.getPaymentStatus = function(paymentId) {
    return Promise.resolve({
        success: true,
        paymentId: paymentId,
        status: 'unknown'
    });
};

/**
 * Обработка ошибок CloudPayments
 */
CloudPaymentsProvider.prototype.handleCloudPaymentsError = function(reason, data) {
    console.error('Cloud Payments error:', reason, data);

    var errorMap = {
        'InsufficientFunds': 'Недостаточно средств на карте',
        'LimitExceeded': 'Превышен лимит по карте',
        'InvalidCardNumber': 'Неверный номер карты',
        'InvalidExpiryDate': 'Неверная дата действия карты',
        'InvalidCvc': 'Неверный CVC код',
        'RejectedByBank': 'Банк отклонил операцию',
        '3DSecureFailed': 'Ошибка 3D-Secure аутентификации',
        'Timeout': 'Время операции истекло',
        'CancelledByUser': 'Операция отменена пользователем',
        'FraudSuspected': 'Подозрение на мошенничество',
        'DuplicateOrderId': 'Заказ с таким номером уже существует',
        'InvalidAmount': 'Неверная сумма платежа'
    };

    var message = errorMap[reason] ||
        (data && data.Message) ||
        (data && data.message) ||
        'Произошла ошибка при оплате';

    var error = new Error(message);
    error.code = reason;
    error.data = data;

    return error;
};

/**
 * Карта ошибок для базового обработчика
 */
CloudPaymentsProvider.prototype.getErrorMap = function() {
    var baseMap = BasePaymentProvider.prototype.getErrorMap.call(this);
    var additionalMap = {
        'InsufficientFunds': 'Недостаточно средств на карте',
        'LimitExceeded': 'Превышен лимит по карте',
        'InvalidCardNumber': 'Неверный номер карты',
        'RejectedByBank': 'Банк отклонил операцию',
        '3DSecureFailed': 'Ошибка 3D-Secure аутентификации',
        'DuplicateOrderId': 'Заказ уже оплачен'
    };

    var merged = {};
    for (var key in baseMap) {
        if (baseMap.hasOwnProperty(key)) {
            merged[key] = baseMap[key];
        }
    }
    for (var key2 in additionalMap) {
        if (additionalMap.hasOwnProperty(key2)) {
            merged[key2] = additionalMap[key2];
        }
    }

    return merged;
};

/**
 * Токенизация карты (для сохранения карт)
 */
CloudPaymentsProvider.prototype.tokenizeCard = function(cardData) {
    var self = this;

    return this.loadCryptogram().then(function() {
        return new Promise(function(resolve, reject) {
            if (!window.cp) {
                reject(new Error('Cryptogram not loaded'));
                return;
            }

            var cryptogram = window.cp.Cryptogram(
                cardData.cardNumber,
                cardData.expiryDate,
                cardData.cvc,
                self.config.credentials.publicKey
            );

            if (cryptogram.success) {
                resolve({
                    token: cryptogram.token,
                    cardType: cryptogram.cardType,
                    firstSix: cryptogram.firstSix,
                    lastFour: cryptogram.lastFour
                });
            } else {
                reject(new Error(cryptogram.message || 'Tokenization failed'));
            }
        });
    });
};

/**
 * Получить компонент виджета для провайдера
 */
CloudPaymentsProvider.prototype.getWidget = function() {
    // Импортируем виджет динамически, чтобы избежать циклических зависимостей
    return require('../../components/payment/widgets/CloudPaymentsWidget').default;
};

/**
 * Имя виджета
 */
CloudPaymentsProvider.prototype.getWidgetName = function() {
    return 'cloud_payments';
};

export { CloudPaymentsProvider };